// Auth state for the SPA. The user store mirrors the server-side session;
// authReady flips to true once bootAuth() resolves, which is the signal that
// route guards can render their decision (otherwise the first paint would
// always show a logged-out shell, then flicker to logged-in once /me lands).
//
// Shape matches App\Http\Resources\UserResource on the API side — keep them
// in sync. Additive changes are safe; removals require a coordinated edit.

import { writable, get } from 'svelte/store';
import { api, ApiError, resetCsrf } from '../api';
import { setFollowing } from './following';

export type DonorTier = 'free' | 'one_time' | 'pro' | 'lifetime';

/**
 * Human-readable labels for donor tiers. Single source of truth for
 * any UI that names a tier (account settings, profile page, etc.).
 * The icon component handles its own aria-labels — these are for
 * visible text alongside the icon.
 */
export const DONOR_TIER_LABEL: Record<DonorTier, string> = {
  free: 'Free',
  one_time: 'One-time supporter',
  pro: 'Pro supporter',
  lifetime: 'Lifetime supporter',
};

export interface UserSettings {
  unit_system?: 'miles' | 'kilometers';
  sound_enabled?: boolean;
  [key: string]: unknown;
}

export interface User {
  id: number;
  username: string;
  email: string;
  pending_email: string | null;
  email_verified_at: string | null;
  donor_tier: DonorTier;
  email_bouncing: boolean;
  settings: UserSettings;
  created_at: string;
}

export const user = writable<User | null>(null);
export const authReady = writable(false);

/**
 * Hit /api/auth/me once on app boot so guards know whether a session exists.
 * Always resolves — network errors leave the user as null but still flip
 * authReady so the UI can render the logged-out path instead of hanging.
 *
 * Also hydrates the `following` store from the same response so
 * FollowButtons render with correct state from the first paint without
 * an extra round-trip per page.
 */
export async function bootAuth(): Promise<void> {
  try {
    const res = await api<{ user: User | null; following: string[] }>('/api/auth/me');
    user.set(res.user);
    setFollowing(res.following ?? []);
  } catch {
    user.set(null);
    setFollowing([]);
  } finally {
    authReady.set(true);
  }
}

/**
 * Drop the local session and force CSRF re-prime on the next mutation.
 * Components should call this after a successful POST /logout — the server
 * has already invalidated the session, this just syncs local state.
 */
export function clearAuth(): void {
  user.set(null);
  setFollowing([]);
  resetCsrf();
}

/**
 * Synchronous read for code paths that can't subscribe (event handlers,
 * one-shot guards). Prefer subscribing in Svelte components.
 */
export function currentUser(): User | null {
  return get(user);
}

export { ApiError };
